import React from "react";
import styled from "styled-components";

export type KpiTone = "brand" | "green" | "yellow" | "red" | "grey";

export type KpiItem = {
  icon: string;
  value: React.ReactNode;
  label: string;
  tone?: KpiTone;
};

const TONES: Record<KpiTone, { bg: string; fg: string }> = {
  brand: { bg: "var(--brand-50)", fg: "var(--brand-700)" },
  green: { bg: "#E6F4EA", fg: "#137333" },
  yellow: { bg: "#FEF7E0", fg: "#B06000" },
  red: { bg: "#FCE8E6", fg: "#C5221F" },
  grey: { bg: "#F1F3F4", fg: "#5F6368" },
};

const Strip = styled.div<{ $cols: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$cols}, minmax(0, 1fr));
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: 16px;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 22px;
  border-right: 1px solid #eef1f4;
  min-width: 0;

  &:last-child {
    border-right: 0;
  }
  @media (max-width: 1024px) {
    &:nth-child(2n) {
      border-right: 0;
    }
    /* Linha divisória em todas; a da última linha some sob a borda do quadro. */
    border-bottom: 1px solid #eef1f4;
    margin-bottom: -1px;
  }
  /* Celular: ícone em cima, número e rótulo embaixo (o rótulo ganha a largura toda). */
  @media (max-width: 599.95px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 14px 16px;
  }
`;

const Icon = styled.span<{ $tone: KpiTone }>`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${(p) => TONES[p.$tone].bg};
  color: ${(p) => TONES[p.$tone].fg};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  .material-icons {
    font-size: 22px;
  }
  @media (max-width: 599.95px) {
    width: 36px;
    height: 36px;
    border-radius: 10px;
  }
`;

const Text = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  .value {
    font-family: var(--font-sans);
    font-size: 24px;
    font-weight: 500;
    letter-spacing: -0.5px;
    color: var(--ink);
    line-height: 1.1;
  }
  .label {
    font-size: 12px;
    color: var(--text-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  /* No celular o rótulo quebra linha em vez de virar "em acom...". */
  @media (max-width: 1024px) {
    .label {
      white-space: normal;
      overflow-wrap: break-word;
      line-height: 1.3;
    }
  }
  @media (max-width: 599.95px) {
    .value {
      font-size: 22px;
    }
  }
`;

/** Faixa de indicadores no topo das listas (redesign V3). */
export const KpiStrip: React.FC<{ items: KpiItem[]; "aria-label"?: string }> = ({
  items,
  "aria-label": ariaLabel,
}) => (
  <Strip $cols={items.length} role="list" aria-label={ariaLabel}>
    {items.map((item) => (
      <Item key={item.label} role="listitem">
        <Icon $tone={item.tone ?? "brand"} aria-hidden="true">
          <span className="material-icons">{item.icon}</span>
        </Icon>
        <Text>
          <span className="value">{item.value}</span>
          <span className="label">{item.label}</span>
        </Text>
      </Item>
    ))}
  </Strip>
);
